/* Pia liest vor (30.10.2026, Betreiber: „Stimme des Geräts“). NUR die Sprachausgabe des Geräts (Web Speech API, speechSynthesis):
   kein Server, kein Anbieter, der Text verlässt das Gerät nicht. Darum nur Stimmen mit `localService` — Online-Stimmen (Edge „Natural“,
   Chrome „Google Deutsch“) schicken den Text an Microsoft bzw. Google und bleiben draußen. Einstellungen je Gerät (localStorage). */
import { useEffect, useState } from 'react';

const K_MODUS = 'p1_pia_vorlesen';        // aus | sprache | immer
const K_STIMME = 'p1_pia_stimme';         // voiceURI
const K_TEMPO = 'p1_pia_tempo';           // 0.8 … 1.2

export const MODI = [
  ['sprache', 'Nach Spracheingabe', 'Wenn du Pia per Mikrofon fragst, liest sie die Antwort vor.'],
  ['immer', 'Jede Antwort', 'Pia liest jede Antwort auf deine Fragen vor, auch getippte.'],
  ['aus', 'Aus', 'Nur über den Lautsprecher-Knopf an der Nachricht.'],
];

const lesen = (k, std) => { try { return localStorage.getItem(k) ?? std; } catch (e) { return std; } };
const schreiben = (k, v) => { try { localStorage.setItem(k, String(v)); } catch (e) { /* ohne Speicher: gilt nur jetzt */ } };

export const verfuegbar = () => typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.SpeechSynthesisUtterance === 'function';
export const modus = () => { const m = lesen(K_MODUS, 'sprache'); return MODI.some(([k]) => k === m) ? m : 'sprache'; };
export const modusSetzen = (m) => schreiben(K_MODUS, m);
export const tempo = () => { const t = parseFloat(lesen(K_TEMPO, '1')); return t >= 0.8 && t <= 1.2 ? t : 1; };
export const tempoSetzen = (t) => schreiben(K_TEMPO, t);
export const stimmeSetzen = (uri) => schreiben(K_STIMME, uri || '');

/* Bekannte Namen der Gerätestimmen (Apple, Windows, Android). Unbekannte stehen in der Mitte, Männerstimmen am Ende. */
const FRAUEN = ['anna', 'petra', 'helena', 'marlene', 'vicki', 'katja', 'hedda', 'amala', 'seraphina', 'louisa', 'elke'];
const MAENNER = ['markus', 'yannick', 'viktor', 'martin', 'stefan', 'conrad', 'hans', 'eddy', 'grandpa', 'großvater', 'reed', 'rocko', 'albert', 'jacques'];
const SPIELEREI = ['eddy', 'flo', 'grandma', 'grandpa', 'großmutter', 'großvater', 'reed', 'rocko', 'sandy', 'shelley', 'bad news', 'bahh', 'bells', 'boing', 'bubbles', 'cellos', 'good news', 'jester', 'organ', 'superstar', 'trinoids', 'whisper', 'wobble', 'zarvox', 'albert', 'fred', 'junior', 'kathy', 'ralph'];

/* Apples Spaß-Stimmen (Grandma, Rocko …) klingen nicht nach Pia und stehen nur zur Wahl, wenn es nichts anderes gibt. */
export const istSpielerei = (v) => SPIELEREI.includes((v?.name || '').toLowerCase().split(/\s*\(/)[0].trim());

export function bewerten(v) {
  const name = (v?.name || '').toLowerCase();
  let p = 0;
  if (/premium/.test(name)) p += 40;
  else if (/erweitert|enhanced|verbessert|neural|natural/.test(name)) p += 25;
  if (FRAUEN.some((f) => name.includes(f))) p += 20;
  if (MAENNER.some((m) => name.includes(m))) p -= 30;
  if (istSpielerei(v)) p -= 60;
  if ((v?.lang || '').toLowerCase().replace('_', '-') === 'de-de') p += 3;
  if (v?.default) p += 1;
  return p;
}

/* Deutsche Stimmen, die auf dem Gerät laufen, beste zuerst. */
export function deutscheStimmen(alle) {
  const de = (alle || [])
    .filter((v) => (v.lang || '').toLowerCase().startsWith('de') && v.localService !== false)
    .sort((a, b) => bewerten(b) - bewerten(a) || a.name.localeCompare(b.name));
  const ernst = de.filter((v) => !istSpielerei(v));
  return ernst.length ? ernst : de;
}

export function istMaennlich(v) {
  const name = (v?.name || '').toLowerCase();
  return MAENNER.some((m) => name.includes(m));
}

function gewaehlteStimme() {
  if (!verfuegbar()) return null;
  const liste = deutscheStimmen(window.speechSynthesis.getVoices());
  const uri = lesen(K_STIMME, '');
  return liste.find((v) => v.voiceURI === uri) || liste[0] || null;
}

/* Text so umbauen, dass er sich gut anhört: keine Links, Zeichen oder Emojis vorlesen, Abkürzungen ausschreiben. */
const ABKUERZUNGEN = [
  [/\bz\.\s?B\./g, 'zum Beispiel'], [/\bd\.\s?h\./g, 'das heißt'], [/\bu\.\s?a\./g, 'unter anderem'], [/\bbzw\./g, 'beziehungsweise'],
  [/\bca\./g, 'circa'], [/\bggf\./g, 'gegebenenfalls'], [/\binkl\./g, 'inklusive'], [/\bvgl\./g, 'vergleiche'], [/\busw\./g, 'und so weiter'],
  [/\bMo\.(?=\s|,|$)/g, 'Montag'], [/\bDi\.(?=\s|,|$)/g, 'Dienstag'], [/\bMi\.(?=\s|,|$)/g, 'Mittwoch'], [/\bDo\.(?=\s|,|$)/g, 'Donnerstag'],
  [/\bFr\.(?=\s|,|$)/g, 'Freitag'], [/\bSa\.(?=\s|,|$)/g, 'Samstag'], [/\bSo\.(?=\s|,|$)/g, 'Sonntag'], [/\bMin\./g, 'Minuten'], [/\bStd\./g, 'Stunden'],
  [/\bPT\b/g, 'Personal Training'], [/\bBMT\b/g, 'Bang Muay Thai'],
];

export function zumSprechen(text) {
  let t = String(text || '');
  t = t.replace(/https?:\/\/\S+/g, '').replace(/(^|\s)\/[a-z][\w\-/]*/g, '$1');      // Links und CRM-Pfade
  t = t.replace(/\p{Extended_Pictographic}|️|‍/gu, '');
  t = t.replace(/[*_`#>|~]+/g, '');                                                       // Markdown
  t = t.replace(/^[ \t]*[-•·][ \t]+/gm, '').replace(/^[ \t]*\d+\.[ \t]+/gm, '');               // Aufzählungszeichen
  t = t.replace(/\s*[→⇒➜]\s*/g, ', ').replace(/[ \t]+[–—][ \t]+/g, ', ').replace(/[ \t]+-[ \t]+/g, ', ');
  t = t.replace(/(\d)\s?€/g, '$1 Euro').replace(/€\s?(\d)/g, '$1 Euro ').replace(/(\d)\s?%/g, '$1 Prozent');
  for (const [re, ersatz] of ABKUERZUNGEN) t = t.replace(re, ersatz);
  t = t.replace(/\n{2,}/g, '. ').replace(/\n/g, '. ');                                   // Zeilen = kurze Pause
  t = t.replace(/([.!?:;,])(\s*[.,])+/g, '$1').replace(/\s{2,}/g, ' ');
  return t.trim();
}

/* In Sätze teilen (Chrome bricht lange Äußerungen nach etwa 15 Sekunden ab). */
export function stuecke(text, max = 220) {
  const saetze = (text.match(/[^.!?]+[.!?]*(\s|$)/g) || [text]).map((s) => s.trim()).filter(Boolean);
  const aus = [];
  let akt = '';
  for (const s of saetze) {
    if (s.length > max) {                                                   // überlanger Satz: an Kommas teilen
      if (akt) { aus.push(akt); akt = ''; }
      let teil = '';
      for (const k of s.split(/(?<=,)\s+/)) {
        if ((teil + ' ' + k).trim().length > max && teil) { aus.push(teil); teil = k; } else teil = (teil + ' ' + k).trim();
      }
      if (teil) aus.push(teil);
    } else if ((akt + ' ' + s).trim().length > max) { aus.push(akt); akt = s; } else akt = (akt + ' ' + s).trim();
  }
  if (akt) aus.push(akt);
  return aus;
}

/* Wer gerade spricht (Nachrichten-ID), damit der Knopf an der Nachricht „Stopp“ zeigen kann. */
let aktuell = null;
const hoerer = new Set();
const melden = (id) => { aktuell = id; hoerer.forEach((f) => f(id)); };

export function stoppen() {
  if (!verfuegbar()) return;
  try { window.speechSynthesis.cancel(); } catch (e) { /* egal */ }
  melden(null);
}

/* iPhone/iPad sprechen nur, wenn das erste Sprechen aus einem Tippen kommt. In jedem Klick aufrufen, nach dem Pia antworten wird. */
let entsperrt = false;
export function entsperren() {
  if (entsperrt || !verfuegbar()) return;
  try {
    const u = new window.SpeechSynthesisUtterance(' ');
    u.volume = 0;
    window.speechSynthesis.speak(u);
    entsperrt = true;
  } catch (e) { /* egal */ }
}

/* Liest `text` vor; ein zweiter Aufruf bricht den ersten ab. `id` = Nachricht (für den Knopf). */
export function vorlesen(text, id = 'probe', { stimme = null, rate = null } = {}) {
  if (!verfuegbar()) return false;
  const teile = stuecke(zumSprechen(text));
  if (!teile.length) return false;
  const synth = window.speechSynthesis;
  try { synth.cancel(); } catch (e) { /* egal */ }
  const v = stimme || gewaehlteStimme();
  const r = rate || tempo();
  melden(id);
  teile.forEach((t, i) => {
    const u = new window.SpeechSynthesisUtterance(t);
    u.lang = v?.lang || 'de-DE';
    if (v) u.voice = v;
    u.rate = r;
    u.pitch = 1;
    if (i === teile.length - 1) { u.onend = () => { if (aktuell === id) melden(null); }; }
    u.onerror = () => { if (aktuell === id) melden(null); };
    synth.speak(u);
  });
  if (synth.paused) synth.resume();                                       // Safari bleibt nach einem Abbruch manchmal pausiert
  return true;
}

/* Vorlesen, während die Antwort noch kommt (03.11.2026, Betreiber: „schneller … natürlicher und flüssiger“). Der erste fertige Satz
   spricht sofort; was danach kommt, sammelt sich, solange Pia spricht, und geht dann als EIN Stück hinaus — weniger Pausen zwischen
   kurzen Sätzen. Gibt `fertigeSaetze` den bis zur letzten Satzgrenze fertigen Text und den Rest zurück. */
const KEIN_SATZENDE = new Set(['z', 'b', 'd', 'h', 'u', 'a', 'bzw', 'ca', 'ggf', 'inkl', 'vgl', 'usw', 'mo', 'di', 'mi', 'do', 'sa',
  'min', 'std', 'nr', 'str', 'tel', 'dr', 'hr', 'fr', 'evtl', 'max', 'mind']);

export function fertigeSaetze(text) {
  const t = String(text || '');
  const re = /[.!?…]+(?=\s)|\n+/g;
  let ende = -1;
  let m;
  while ((m = re.exec(t))) {
    if (m[0][0] === '.') {
      const wort = (t.slice(0, m.index).match(/(\S+)$/) || ['', ''])[1];
      if (/\d$/.test(wort) || KEIN_SATZENDE.has(wort.toLowerCase().replace(/^.*\./, ''))) continue;      // „15. November“, „z. B.“
    }
    ende = m.index + m[0].length;
  }
  return ende < 0 ? ['', t] : [t.slice(0, ende), t.slice(ende)];
}

export function sprecher(start = 'live') {
  if (!verfuegbar()) return null;
  const synth = window.speechSynthesis;
  try { synth.cancel(); } catch (e) { /* egal */ }
  const v = gewaehlteStimme();
  const r = tempo();
  let id = start;
  let puffer = '';          // angefangener Satz
  let bereit = '';          // fertige Sätze, die warten, bis Pia ausgeredet hat
  let offen = 0;
  let fertig = false;
  let aus = false;
  melden(id);
  const fertigMelden = () => { if (fertig && offen <= 0 && !bereit.trim() && aktuell === id) melden(null); };
  const los = () => {
    if (aus || offen > 0 || !bereit.trim()) return;
    const teile = stuecke(zumSprechen(bereit));
    bereit = '';
    teile.forEach((stueck) => {
      const u = new window.SpeechSynthesisUtterance(stueck);
      u.lang = v?.lang || 'de-DE';
      if (v) u.voice = v;
      u.rate = r;
      u.pitch = 1;
      offen += 1;
      const weiter = () => { offen -= 1; if (offen <= 0) { los(); fertigMelden(); } };
      u.onend = weiter;
      u.onerror = weiter;
      synth.speak(u);
    });
    if (synth.paused) synth.resume();
  };
  return {
    dazu(stueck) {
      if (aus) return;
      puffer += stueck;
      const [satz, rest] = fertigeSaetze(puffer);
      if (satz.trim()) { bereit += satz; puffer = rest; los(); }
    },
    ende() {
      if (aus) return;
      fertig = true;
      bereit += puffer;
      puffer = '';
      los();
      fertigMelden();
    },
    abbrechen() {
      aus = true;
      try { synth.cancel(); } catch (e) { /* egal */ }
      if (aktuell === id) melden(null);
    },
    /* Die Nachricht ist gespeichert: der Lautsprecher an IHR zeigt jetzt „Stopp“. */
    heisst(neu) { if (aktuell === id) { id = neu; melden(neu); } else id = neu; },
  };
}

export function useSprichtGerade() {
  const [id, setId] = useState(aktuell);
  useEffect(() => { hoerer.add(setId); return () => { hoerer.delete(setId); }; }, []);
  return id;
}

/* Stimmen kommen in Chrome erst nach `voiceschanged`. */
export function useStimmen() {
  const [liste, setListe] = useState(() => (verfuegbar() ? deutscheStimmen(window.speechSynthesis.getVoices()) : []));
  useEffect(() => {
    if (!verfuegbar()) return undefined;
    const neu = () => setListe(deutscheStimmen(window.speechSynthesis.getVoices()));
    neu();
    window.speechSynthesis.addEventListener?.('voiceschanged', neu);
    const t = setTimeout(neu, 800);
    return () => { clearTimeout(t); window.speechSynthesis.removeEventListener?.('voiceschanged', neu); };
  }, []);
  return liste;
}

export const gewaehlteUri = () => lesen(K_STIMME, '');
